import {
  ActionIcon, Badge, Button, Card, Divider, Drawer, Group, Menu, MultiSelect, Pagination, PasswordInput, Select, Stack, Switch, Table,
  TagsInput, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconDots, IconKey, IconLogout, IconPlus, IconShieldLock, IconTrash, IconUserCheck, IconUserOff,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, unwrap } from "../../api/client";
import { http } from "../../api/http";
import { useOpstina } from "../../context/opstina";
import type { components } from "../../api/schema";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

type User = components["schemas"]["UserOut"];
type UserCreate = components["schemas"]["UserCreate"];

const PAGE = 25;

export default function UsersPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dq] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<User | "new" | null>(null);

  const users = useQuery({
    queryKey: ["admin-users", dq, page],
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/users", { params: { query: { search: dq || undefined, page, size: PAGE } } })),
  });
  const roles = useQuery({ queryKey: ["admin-roles"], queryFn: async () => unwrap(await api.GET("/api/v1/admin/roles")) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const ok = (m: string) => notifications.show({ color: "green", message: t(m) });
  const fail = (e: unknown) => notifications.show({ color: "red", message: String(e) });

  const toggle = useMutation({
    mutationFn: async (u: User) => unwrap(await api.PATCH("/api/v1/admin/users/{user_id}", {
      params: { path: { user_id: u.id } }, body: { enabled: !u.enabled } })),
    onSuccess: () => { refresh(); ok("Sačuvano"); }, onError: fail,
  });
  const del = useMutation({
    mutationFn: async (u: User) => unwrap(await api.DELETE("/api/v1/admin/users/{user_id}", { params: { path: { user_id: u.id } } })),
    onSuccess: () => { refresh(); ok("Korisnik obrisan"); }, onError: fail,
  });
  const action = async (u: User, a: "logout" | "mfa") => {
    try {
      if (a === "logout") unwrap(await api.POST("/api/v1/admin/users/{user_id}/logout", { params: { path: { user_id: u.id } } }));
      else unwrap(await api.DELETE("/api/v1/admin/users/{user_id}/mfa", { params: { path: { user_id: u.id } } }));
      ok(a === "logout" ? "Korisnik je odjavljen sa svih sesija" : "MFA resetovan — korisnik će ponovo podesiti OTP");
    } catch (e) { fail(e); }
  };

  return (
    <AdminLayout>
      <Stack>
        <Group justify="space-between">
          <div>
            <Title order={3}>{t("Korisnici")}</Title>
            <Text size="sm" c="dimmed">{t("Nalozi se vode u Keycloak-u; ovdje se dodjeljuju poslovne role i MFA.")}</Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing("new")}>{t("Novi korisnik")}</Button>
        </Group>
        <TextInput placeholder={t("Pretraga po imenu, korisničkom imenu ili emailu")} value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }} maw={420} />
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("Korisnik")}</Table.Th><Table.Th>Email</Table.Th><Table.Th>{t("Role")}</Table.Th>
                <Table.Th>{t("Opseg")}</Table.Th><Table.Th>MFA</Table.Th><Table.Th>{t("Status")}</Table.Th><Table.Th w={60} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(users.data?.items ?? []).map((u) => (
                <Table.Tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setEditing(u)}>
                  <Table.Td>
                    <Text fw={600} size="sm">{[u.first_name, u.last_name].filter(Boolean).join(" ") || u.username}</Text>
                    <Text size="xs" c="dimmed">{u.username}</Text>
                  </Table.Td>
                  <Table.Td><Text size="sm">{u.email}</Text></Table.Td>
                  <Table.Td><Group gap={4}>{u.roles?.map((r) => <Badge key={r} size="sm" variant="light">{r}</Badge>)}</Group></Table.Td>
                  <Table.Td><ScopeBadges attrs={(u.attributes ?? {}) as Record<string, string[]>} /></Table.Td>
                  <Table.Td>{u.required_actions?.includes("CONFIGURE_TOTP")
                    ? <Badge size="sm" color="orange" variant="light">{t("čeka podešavanje")}</Badge> : <Text size="xs" c="dimmed">—</Text>}</Table.Td>
                  <Table.Td>{u.enabled ? <Badge color="green" size="sm">{t("aktivan")}</Badge>
                    : <Badge color="gray" size="sm">{t("blokiran")}</Badge>}</Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Menu position="bottom-end">
                      <Menu.Target><ActionIcon variant="subtle"><IconDots size={16} /></ActionIcon></Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={u.enabled ? <IconUserOff size={16} /> : <IconUserCheck size={16} />}
                          onClick={() => toggle.mutate(u)}>{u.enabled ? t("Blokiraj") : t("Aktiviraj")}</Menu.Item>
                        <Menu.Item leftSection={<IconShieldLock size={16} />} onClick={() => action(u, "mfa")}>{t("Reset MFA")}</Menu.Item>
                        <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => action(u, "logout")}>{t("Odjavi sa svih sesija")}</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item color="red" leftSection={<IconTrash size={16} />}
                          onClick={() => window.confirm(t("Obrisati korisnika?")) && del.mutate(u)}>{t("Obriši")}</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Pagination total={Math.max(1, Math.ceil((users.data?.total ?? 0) / PAGE))} value={page} onChange={setPage} />
        <UserDrawer user={editing} roleOptions={(roles.data ?? []).map((r) => ({ value: r.name, label: r.description ?? r.name }))}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      </Stack>
    </AdminLayout>
  );
}

function UserDrawer({ user, roleOptions, onClose, onSaved }: {
  user: User | "new" | null; roleOptions: { value: string; label: string }[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useT();
  const isNew = user === "new";
  const u = user && user !== "new" ? user : null;
  const detail = useQuery({
    queryKey: ["admin-user", u?.id], enabled: !!u,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/users/{user_id}", { params: { path: { user_id: u!.id } } })),
  });
  const [form, setForm] = useState<Partial<UserCreate> & { password2?: string }>({});
  const [busy, setBusy] = useState(false);
  // organizaciona jedinica dolazi iz Keycloak atributa (attributes.organization[0])
  const attrs = (u as { attributes?: Record<string, string[]> } | null)?.attributes;
  const v = { ...(u ?? {}), organization: attrs?.organization?.[0], ...form } as UserCreate & { password2?: string };
  const set = (k: string, val: unknown) => setForm((f) => ({ ...f, [k]: val }));

  async function save() {
    setBusy(true);
    try {
      if (isNew) {
        unwrap(await api.POST("/api/v1/admin/users", { body: {
          username: v.username, email: v.email || null, first_name: v.first_name, last_name: v.last_name,
          password: v.password || null, temporary_password: v.temporary_password ?? true,
          roles: v.roles ?? ["registrovani_korisnik"], require_mfa: v.require_mfa ?? false, enabled: true, group_ids: [],
          organization: v.organization,
        } }));
      } else if (u) {
        unwrap(await api.PATCH("/api/v1/admin/users/{user_id}", { params: { path: { user_id: u.id } }, body: {
          email: form.email, first_name: form.first_name, last_name: form.last_name, require_mfa: form.require_mfa,
          organization: form.organization } }));
        if (form.roles) unwrap(await api.PUT("/api/v1/admin/users/{user_id}/roles", { params: { path: { user_id: u.id } }, body: { roles: form.roles } }));
        if (form.password) unwrap(await api.POST("/api/v1/admin/users/{user_id}/reset-password", {
          params: { path: { user_id: u.id } }, body: { password: form.password, temporary: form.temporary_password ?? true } }));
      }
      notifications.show({ color: "green", message: t("Sačuvano") });
      setForm({});
      onSaved();
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    } finally { setBusy(false); }
  }

  return (
    <Drawer opened={!!user} onClose={() => { setForm({}); onClose(); }} position="right" size="lg"
      title={<Text fw={700} size="lg">{isNew ? t("Novi korisnik") : u?.username}</Text>}>
      <Stack>
        {isNew && <TextInput label={t("Korisničko ime")} required value={v.username ?? ""} onChange={(e) => set("username", e.currentTarget.value)} />}
        <Group grow>
          <TextInput label={t("Ime")} value={v.first_name ?? ""} onChange={(e) => set("first_name", e.currentTarget.value)} />
          <TextInput label={t("Prezime")} value={v.last_name ?? ""} onChange={(e) => set("last_name", e.currentTarget.value)} />
        </Group>
        <TextInput label="Email" value={v.email ?? ""} onChange={(e) => set("email", e.currentTarget.value)} />
        <TextInput label={t("Organizaciona jedinica")} value={(v.organization as string) ?? ""} onChange={(e) => set("organization", e.currentTarget.value)} />
        <MultiSelect label={t("Poslovne role")} data={roleOptions} value={v.roles ?? (isNew ? ["registrovani_korisnik"] : [])}
          onChange={(r) => set("roles", r)} />
        <PasswordInput label={isNew ? t("Lozinka (min. 10 znakova)") : t("Nova lozinka (opciono)")} value={v.password ?? ""}
          onChange={(e) => set("password", e.currentTarget.value)} />
        <Switch label={t("Privremena lozinka — korisnik je mijenja pri prvoj prijavi")} checked={v.temporary_password ?? true}
          onChange={(e) => set("temporary_password", e.currentTarget.checked)} />
        <Switch label={t("Obavezna MFA (OTP aplikacija)")} checked={form.require_mfa ?? (u?.required_actions?.includes("CONFIGURE_TOTP") ?? false)}
          onChange={(e) => set("require_mfa", e.currentTarget.checked)} />
        {u && <ScopeEditor userId={u.id} roleOptions={roleOptions} />}
        {detail.data && (
          <Stack gap={4}>
            <Group gap={6}><IconKey size={16} />
              <Text size="sm">{t("MFA podešen")}: <b>{detail.data.mfa_configured ? t("da") : t("ne")}</b></Text></Group>
            <Text size="sm" fw={600} mt="xs">{t("Efektivne permisije")}</Text>
            <Group gap={4} translate="no">{detail.data.permissions?.map((p) => (
              <Tooltip key={p} label={p}><Badge size="xs" variant="outline">{p}</Badge></Tooltip>))}</Group>
            {detail.data.groups?.length ? <Text size="xs" c="dimmed">{t("Grupe")}: {detail.data.groups.join(", ")}</Text> : null}
          </Stack>
        )}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>{t("Odustani")}</Button>
          <Button onClick={save} loading={busy}>{t("Sačuvaj")}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}


function ScopeBadges({ attrs }: { attrs: Record<string, string[]> }) {
  const { t } = useT();
  const { unitName } = useOpstina();
  const op = attrs.opstine ?? [], bpg = attrs.bpg ?? [], dj = attrs.dodjele ?? [];
  if (!op.length && !bpg.length && !dj.length) return <Text size="xs" c="dimmed">{t("prema roli")}</Text>;
  return (
    <Group gap={4}>
      {op.map((o) => <Badge key={o} size="xs" variant="light" color="blue">{unitName(o)}</Badge>)}
      {bpg.map((b) => <Badge key={b} size="xs" variant="light" color="soil">BPG {b}</Badge>)}
      {dj.map((d) => { const [r, o] = d.split("@"); return <Badge key={d} size="xs" variant="outline" color="grape">{r} @ {unitName(o)}</Badge>; })}
    </Group>
  );
}

interface ScopeData { opstine: string[]; bpg: string[]; dodjele: { role: string; opstina: string }[] }

/** Opseg korisnika: opštine za globalne role, BPG (proizvođač) i role dodijeljene samo u pojedinoj opštini. */
function ScopeEditor({ userId, roleOptions }: { userId: string; roleOptions: { value: string; label: string }[] }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { units } = useOpstina();
  const q = useQuery({ queryKey: ["user-scope", userId], queryFn: () => http.get<ScopeData>(`/api/v1/admin/users/${userId}/scope`) });
  const [d, setD] = useState<ScopeData | null>(null);
  const v = d ?? q.data ?? { opstine: [], bpg: [], dodjele: [] };
  const save = useMutation({
    mutationFn: () => http.put<ScopeData>(`/api/v1/admin/users/${userId}/scope`, v),
    onSuccess: () => { setD(null); qc.invalidateQueries({ queryKey: ["user-scope", userId] }); qc.invalidateQueries({ queryKey: ["admin-users"] });
      notifications.show({ color: "green", message: t("Opseg sačuvan — važi od sljedeće prijave korisnika") }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const unitData = units.map((u) => ({ value: u.code, label: u.name }));
  return (
    <Card withBorder p="sm">
      <Text fw={600} size="sm">{t("Opseg po opštinama")}</Text>
      <Text size="xs" c="dimmed" mb="xs">{t("Bez ograničenja korisnik vidi sve što mu rola dozvoljava (sve opštine samo uz ovlašćenje scope:all). Opštine ograničavaju globalne role; dodjele daju rolu samo u jednoj opštini.")}</Text>
      <Stack gap="xs">
        <MultiSelect label={t("Opštine u nadležnosti (globalne role)")} searchable clearable data={unitData} value={v.opstine}
          onChange={(x) => setD({ ...v, opstine: x })} />
        <TagsInput label={t("BPG gazdinstava (proizvođač)")} value={v.bpg} onChange={(x) => setD({ ...v, bpg: x })} placeholder="npr. 2010100001" />
        <Divider label={t("Role po opštini")} labelPosition="left" />
        {v.dodjele.map((a, i) => (
          <Group key={i} gap="xs" wrap="nowrap">
            <Select size="xs" style={{ flex: 1 }} data={roleOptions} value={a.role} onChange={(r) => setD({ ...v, dodjele: v.dodjele.map((x, j) => (j === i ? { ...x, role: r ?? "" } : x)) })} />
            <Text size="xs">@</Text>
            <Select size="xs" style={{ flex: 1 }} searchable data={unitData} value={a.opstina} onChange={(o) => setD({ ...v, dodjele: v.dodjele.map((x, j) => (j === i ? { ...x, opstina: o ?? "" } : x)) })} />
            <ActionIcon color="red" variant="subtle" onClick={() => setD({ ...v, dodjele: v.dodjele.filter((_, j) => j !== i) })}><IconTrash size={14} /></ActionIcon>
          </Group>
        ))}
        <Group justify="space-between">
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setD({ ...v, dodjele: [...v.dodjele, { role: roleOptions[0]?.value ?? "", opstina: units[0]?.code ?? "" }] })}>{t("Dodaj rolu u opštini")}</Button>
          <Button size="xs" onClick={() => save.mutate()} disabled={!d} loading={save.isPending}>{t("Sačuvaj opseg")}</Button>
        </Group>
        <Text size="xs" c="dimmed">{t("Administrativna ovlašćenja (korisnici, role, šifarnici, parametri, backup) ne mogu se dodijeliti po opštini.")}</Text>
      </Stack>
    </Card>
  );
}
